// جدول رویدادهای Audit (AP-IAM-04): زبانه «ممیزی عملیات» SCR-STAFF-925 با فیلترهای کامل AC-IAM-062.1 و خروجی CSV (AC-IAM-062.2؛ خود
// خروجی Audit می‌شود) و جدول مشترک گزارش دسترسی به پرونده SCR-STAFF-926 (کاربر، نقش، زمان، عملیات، دسترسی اضطراری — AC-IAM-061.1).
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type AuditEventView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, JalaliDate, JalaliDatePickerRHF, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { iamKeys, useRoleLabel, useRoles } from './common';

export const AUDIT_MODULES = ['record', 'iam', 'plt', 'pat', 'sch', 'cat', 'org', 'cfg', 'int'] as const;
export const AUDIT_RESULTS = ['Success', 'Denied', 'Failed'] as const;

const PAGE_SIZE = 50;
/** آغاز روز کسب‌وکاری تهران (بدون DST از ۱۴۰۱) برای فیلتر بازه. */
const TEHRAN_OFFSET = '+03:30';

function dayStart(date: string): string {
  return `${date}T00:00:00${TEHRAN_OFFSET}`;
}

function nextDayStart(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1));
  return dayStart(next.toISOString().slice(0, 10));
}

/** ذخیره فایل در مرورگر (بی‌اثر در محیط بدون URL.createObjectURL). */
export function downloadBlob(blob: Blob, fileName: string) {
  if (typeof URL.createObjectURL !== 'function') {
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** ستون‌های مشترک رویداد Audit. */
export function useAuditColumns(): DataTableColumn<AuditEventView>[] {
  const { t } = useTranslation('iam');
  const roleLabel = useRoleLabel();
  return [
    { id: 'time', headerKey: 'iam:audit.columns.time', cell: (row) => <JalaliDate value={row.occurredAt} withTime /> },
    {
      id: 'actor',
      headerKey: 'iam:audit.columns.actor',
      cell: (row) => (
        <Stack>
          <span>{row.actorName ?? t('common:states.none')}</span>
          {row.actorType !== 'User' && (
            <Typography variant="caption" color="text.secondary">
              {t(`iam:audit.actor_types.${row.actorType}`, { defaultValue: row.actorType })}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      id: 'roles',
      headerKey: 'iam:audit.columns.roles',
      cell: (row) => ((row.roles ?? []).length > 0 ? (row.roles ?? []).map((code) => roleLabel(code)).join(t('iam:audit.list_separator')) : t('common:states.none')),
    },
    {
      id: 'action',
      headerKey: 'iam:audit.columns.action',
      cell: (row) => (
        <span data-testid={`audit-action-${row.eventId}`} data-action={row.action}>
          {t(`iam:audit.actions.${row.action.replaceAll('.', '_')}`, { defaultValue: row.action })}
        </span>
      ),
    },
    {
      id: 'resource',
      headerKey: 'iam:audit.columns.resource',
      cell: (row) => <bdi dir="ltr">{row.resourceType}</bdi>,
    },
    {
      id: 'result',
      headerKey: 'iam:audit.columns.result',
      cell: (row) => (
        <Chip
          size="small"
          color={row.result === 'Success' ? 'default' : 'error'}
          variant="outlined"
          label={t(`iam:audit.results.${row.result}`, { defaultValue: row.result })}
          data-testid={`audit-result-${row.eventId}`}
        />
      ),
    },
    {
      id: 'override',
      headerKey: 'iam:audit.columns.override',
      cell: (row) =>
        row.accessOverrideId ? (
          <Chip size="small" color="error" label={t('iam:audit.override_badge')} data-testid={`audit-override-${row.eventId}`} />
        ) : null,
    },
  ];
}

interface RangeForm {
  from: string | null;
  to: string | null;
}

/** زبانه «ممیزی عملیات» SCR-STAFF-925 (iam.audit.view؛ خروجی با iam.audit.export). */
export function AuditEventsTab() {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const roles = useRoles();
  const roleLabel = useRoleLabel();
  const columns = useAuditColumns();
  const [module, setModule] = useState('');
  const [result, setResult] = useState('');
  const [role, setRole] = useState('');
  const [action, setAction] = useState('');
  const [overrideOnly, setOverrideOnly] = useState(false);
  const [exportError, setExportError] = useState<unknown>(undefined);
  const [notice, setNotice] = useState<string | null>(null);
  const { control } = useForm<RangeForm>({ defaultValues: { from: null, to: null } });
  const from = useWatch({ control, name: 'from' });
  const to = useWatch({ control, name: 'to' });

  const filters = useMemo(
    () => ({
      module: module || undefined,
      result: result || undefined,
      role: role || undefined,
      action: action.trim() || undefined,
      accessOverride: overrideOnly ? true : undefined,
      from: from ? dayStart(from) : undefined,
      to: to ? nextDayStart(to) : undefined,
    }),
    [module, result, role, action, overrideOnly, from, to],
  );

  const events = useInfiniteQuery({
    queryKey: iamKeys.auditEvents(session?.tenantId, filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(tenantApi.GET('/api/v1/identity/audit-events', { params: { query: { ...filters, cursor: pageParam, limit: PAGE_SIZE } } })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const exportCsv = async () => {
    setExportError(undefined);
    const response = await tenantApi.POST('/api/v1/identity/audit-events:export', {
      body: {
        format: 'csv',
        filter: {
          resourceType: null,
          resourceId: null,
          actorId: null,
          membershipId: null,
          role: filters.role ?? null,
          branchId: null,
          module: filters.module ?? null,
          action: filters.action ?? null,
          patientId: null,
          result: filters.result ?? null,
          accessOverride: filters.accessOverride ?? null,
          accessOverrideId: null,
          actorType: null,
          from: filters.from ?? null,
          to: filters.to ?? null,
        },
      },
      parseAs: 'blob',
    });
    if (response.response.ok && response.data) {
      downloadBlob(response.data as unknown as Blob, 'audit-events.csv');
      setNotice(t('iam:audit.exported'));
    } else {
      setExportError(response.error ?? new Error('export'));
    }
  };

  const select = (labelKey: string, value: string, onChange: (value: string) => void, options: { value: string; label: string }[], testId: string) => (
    <TextField
      select
      label={t(labelKey)}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={{ minWidth: 160 }}
      slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true }, htmlInput: { 'data-testid': testId } }}
    >
      <MenuItem value="">{t('iam:audit.any')}</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <>
      <DataTable
        captionKey="iam:security_events.tabs.audit"
        columns={columns}
        rows={events.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.eventId}
        isLoading={events.isPending}
        error={events.error}
        onRetry={() => void events.refetch()}
        hasMore={events.hasNextPage}
        onLoadMore={() => void events.fetchNextPage()}
        isFetchingMore={events.isFetchingNextPage}
        emptyTitleKey="iam:audit.empty"
        toolbar={
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              {select('iam:audit.filters.module', module, setModule, AUDIT_MODULES.map((value) => ({ value, label: t(`iam:audit.modules.${value}`) })), 'audit-filter-module')}
              {select('iam:audit.filters.result', result, setResult, AUDIT_RESULTS.map((value) => ({ value, label: t(`iam:audit.results.${value}`) })), 'audit-filter-result')}
              {select(
                'iam:audit.filters.role',
                role,
                setRole,
                (roles.data?.items ?? []).map((item) => ({ value: item.code, label: roleLabel(item.code) })),
                'audit-filter-role',
              )}
              <TextField
                label={t('iam:audit.filters.action')}
                value={action}
                onChange={(event) => setAction(event.target.value)}
                slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'audit-filter-action' } }}
              />
              <JalaliDatePickerRHF control={control} name="from" labelKey="iam:audit.filters.from" />
              <JalaliDatePickerRHF control={control} name="to" labelKey="iam:audit.filters.to" />
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <FormControlLabel
                control={<Checkbox checked={overrideOnly} onChange={(event) => setOverrideOnly(event.target.checked)} data-testid="audit-filter-override" />}
                label={t('iam:audit.override_only')}
              />
              {access.can('iam.audit.export') && (
                <Button variant="outlined" onClick={() => void exportCsv()} data-testid="audit-export">
                  {t('iam:audit.export')}
                </Button>
              )}
            </Stack>
          </Stack>
        }
      />
      {exportError ? <ErrorState error={exportError} compact /> : null}
      <Snackbar open={notice !== null} autoHideDuration={4000} onClose={() => setNotice(null)} message={notice} />
    </>
  );
}
